/**
 * Property Details Form Section
 * Collects home and property information
 */

import { Home02Icon, Target02Icon } from "@porchpass/ui";
import * as React from "react";
import { FormTable, FormTableBody } from "@porchpass/ui";
import { HomeFields } from "@porchpass/ui";
import { useApplicationFormStore } from "../hooks/useApplicationFormStore";
import { validateHomeField } from "../utils/validationSchemas";
import type { HomeFieldsFormData } from "@porchpass/ui";

export const HomeDetailsForm: React.FC = () => {
  const store = useApplicationFormStore();
  const home = store.data.home;

  const fieldErrors = React.useMemo(() => {
    const result: Partial<Record<keyof HomeFieldsFormData, string>> = {};
    const mappings: Array<[string, keyof HomeFieldsFormData]> = [
      ["home.modelName", "modelName"],
      ["home.modelYear", "modelYear"],
      ["home.manufacturer", "manufacturer"],
      ["home.homeType", "homeType"],
      ["home.primaryUse", "primaryUse"],
      ["home.usageTypeOther", "usageTypeOther"],
      ["home.manufacturerOther", "manufacturerOther"],
      ["home.homeTypeOther", "homeTypeOther"],
    ];
    for (const [errorKey, fieldKey] of mappings) {
      if (store.fieldErrors[errorKey]) {
        result[fieldKey] = store.fieldErrors[errorKey];
      }
    }
    return result;
  }, [store.fieldErrors]);

  const value: HomeFieldsFormData = {
    manufacturer: home.manufacturer,
    manufacturerOther: home.manufacturerOther,
    modelName: home.modelName,
    modelYear: home.modelYear,
    homeType: home.homeType,
    homeTypeOther: home.homeTypeOther,
    primaryUse: home.primaryUse,
    usageTypeOther: home.usageTypeOther,
  };

  const clearIfValid = (field: string, value: string) => {
    const errorKey = `home.${field}`;
    if (store.fieldErrors[errorKey] && !validateHomeField(field, value)) {
      store.clearFieldError(errorKey);
    }
  };

  const handleChange = (patch: Partial<HomeFieldsFormData>) => {
    for (const key of Object.keys(patch)) {
      const fieldValue = patch[key as keyof HomeFieldsFormData];
      if (
        fieldValue !== undefined &&
        fieldValue !== null &&
        (typeof fieldValue === "string" || typeof fieldValue === "number")
      ) {
        clearIfValid(key, String(fieldValue));
      }
      if (key === "primaryUse" && store.fieldErrors["home.primaryUse"]) {
        store.clearFieldError("home.primaryUse");
      }
      if (key === "homeType" && store.fieldErrors["home.homeType"]) {
        store.clearFieldError("home.homeType");
      }
      if (key === "manufacturer" && store.fieldErrors["home.manufacturer"]) {
        store.clearFieldError("home.manufacturer");
      }
    }
    store.updateHome(patch);
  };

  return (
    <div className="space-y-6">
      <FormTable
        title="Home Details"
        headerIcon={
          <Home02Icon
            variant="bulk-rounded"
            size={16}
            className="text-primary"
          />
        }
      >
        <FormTableBody>
          <HomeFields
            value={value}
            onChange={handleChange}
            errors={fieldErrors}
            showManufactureSection
          />
        </FormTableBody>
      </FormTable>

      <FormTable
        title="Primary Use"
        subtitle="How will this home be used"
        headerIcon={
          <Target02Icon
            variant="bulk-rounded"
            size={16}
            className="text-[#226392]"
          />
        }
      >
        <FormTableBody>
          <HomeFields
            value={value}
            onChange={handleChange}
            errors={fieldErrors}
            showPrimaryUseOnly
          />
        </FormTableBody>
      </FormTable>
    </div>
  );
};

export default HomeDetailsForm;
